import React from 'react';
import {
    BookOpen, FileText, ClipboardList, Target, BarChart2, MessageSquare, User, Users,
    UserCog, BarChart3, ChevronRight, ChevronLeft, Menu, X, LogOut
} from 'lucide-react';
import NavItem from '../components/NavItem';
import CategorySidebar from '../components/navigation/CategorySidebar';
import ThemeSwitcher from '../components/ThemeSwitcher';
import { GlobalSearch } from '../components/common';
import CurrentPeriodIndicator from '../components/CurrentPeriodIndicator';
import NotificationCenter from '../NotificationCenter';
import StudentAlertBanner from '../components/StudentAlertBanner';

const MainLayout = ({
    user,
    page,
    navigate,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    aiModel,
    setAiModel,
    handleLogoutAndNavigate,
    children
}) => {
    return (
        <div className="min-h-screen font-sans flex" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
            {/* Sidebar for Desktop */}
            <aside className={`hidden lg:flex flex-col border-r p-4 shrink-0 shadow-2xl transition-all duration-300 ${isSidebarCollapsed ? 'w-20' : 'w-64'}`} style={{ background: 'var(--sidebar-bg)', borderColor: 'var(--border-color)' }}>
                {user?.role === 'teacher' || (!user?.role || (user?.role !== 'student' && user?.role !== 'admin')) ? (
                    // Regular Teacher Navigation - Use CategorySidebar (includes its own header)
                    <CategorySidebar
                        user={user}
                        page={page}
                        navigate={navigate}
                        isSidebarCollapsed={isSidebarCollapsed}
                        setIsSidebarCollapsed={setIsSidebarCollapsed}
                        handleLogoutAndNavigate={handleLogoutAndNavigate}
                        aiModel={aiModel}
                        setAiModel={setAiModel}
                    />
                ) : (
                    <>
                        {/* Header for Student/Admin */}
                        <div className="flex items-center justify-between mb-8">
                            {!isSidebarCollapsed && (
                                <div className="font-bold text-2xl text-white px-2">
                                    {user?.role === 'student' ? '🎓 Portal Siswa CikGu' : 'CikGu by Al-Fazza'}
                                </div>
                            )}
                            <button
                                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                                className="p-2 rounded-lg text-purple-100 hover:bg-purple-700 hover:text-white transition-colors"
                                title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                            >
                                {isSidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                            </button>
                        </div>
                        <nav className="flex-grow space-y-2">
                            {user?.role === 'student' ? (
                                // Student Navigation
                                <>
                                    <NavItem icon={BookOpen} label="Dashboard" targetPage="siswa_dashboard" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                    <NavItem icon={Users} label="Kelas Saya" targetPage="kelas_saya" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                    <NavItem icon={FileText} label="Soal Ulangan" targetPage="siswa_soal" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                    <NavItem icon={ClipboardList} label="LKPD" targetPage="siswa_lkpd" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                    <NavItem icon={BookOpen} label="Materi Pembelajaran" targetPage="materi" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                    <NavItem icon={Target} label="Quiz Interaktif" targetPage="interactive-quiz" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                    <NavItem icon={Target} label="Games Edukasi" targetPage="games" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                    <NavItem icon={BarChart2} label="Buku Nilai" targetPage="nilai" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                    <NavItem icon={MessageSquare} label="Chat dengan AI" targetPage="chat" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                    <NavItem icon={User} label="Profil Siswa" targetPage="profil" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                </>
                            ) : (
                                // Admin Navigation - Simplified
                                <>
                                    <NavItem icon={BookOpen} label="Halaman Utama" targetPage="dashboard" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                                    <NavItem icon={UserCog} label="Panel Admin" targetPage="admin" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />

                                    <div className="pt-4 mt-4">
                                        <button
                                            onClick={handleLogoutAndNavigate}
                                            className={`flex items-center w-full px-4 py-3 rounded-lg transition-colors text-purple-100 hover:bg-red-600 hover:text-white group ${isSidebarCollapsed ? 'justify-center' : ''}`}
                                            title={isSidebarCollapsed ? "Keluar" : undefined}
                                        >
                                            <LogOut size={20} className={isSidebarCollapsed ? '' : 'mr-4'} />
                                            {!isSidebarCollapsed && <span className="font-medium">Keluar</span>}
                                        </button>
                                    </div>
                                </>
                            )}
                        </nav>
                    </>
                )}
            </aside>

            {/* Mobile Menu Button */}
            <div className="lg:hidden fixed top-4 left-4 z-50">
                <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors">
                    {isMobileMenuOpen ? <X /> : <Menu />}
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsMobileMenuOpen(false)}></div>
            )}

            {/* Mobile Sidebar */}
            <aside className={`fixed top-0 left-0 h-full w-80 sm:w-64 border-r p-4 z-50 transform transition-transform duration-300 ease-in-out lg:hidden flex flex-col overflow-y-auto shadow-2xl ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`} style={{ background: 'var(--sidebar-bg)', borderColor: 'var(--border-color)' }}>
                <nav className="flex-grow space-y-2">
                    {user?.role === 'student' ? (
                        // Student Navigation
                        <>
                            <div className="font-bold text-2xl text-white mb-8 px-2">
                                🎓 Portal Siswa CikGu
                            </div>
                            <NavItem icon={BookOpen} label="Dashboard" targetPage="siswa_dashboard" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                            <NavItem icon={Users} label="Kelas Saya" targetPage="kelas_saya" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                            <NavItem icon={FileText} label="Soal Ulangan" targetPage="siswa_soal" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                            <NavItem icon={ClipboardList} label="LKPD" targetPage="siswa_lkpd" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                            <NavItem icon={BookOpen} label="Materi Pembelajaran" targetPage="materi" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                            <NavItem icon={Target} label="Quiz Interaktif" targetPage="interactive-quiz" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                            <NavItem icon={Target} label="Games Edukasi" targetPage="games" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                            <NavItem icon={BarChart2} label="Buku Nilai" targetPage="nilai" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                            <NavItem icon={MessageSquare} label="Chat dengan AI" targetPage="chat" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                            <NavItem icon={User} label="Profil Siswa" targetPage="profil" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                        </>
                    ) : user?.role === 'admin' ? (
                        // Admin Navigation - Simplified
                        <>
                            <div className="font-bold text-2xl text-white mb-8 px-2">
                                CikGu by Al-Fazza
                            </div>
                            <NavItem icon={BookOpen} label="Dashboard utama" targetPage="dashboard" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />
                            <NavItem icon={UserCog} label="Panel Admin" targetPage="admin" page={page} navigate={navigate} isSidebarCollapsed={isSidebarCollapsed} user={user} />

                            <div className="pt-4 mt-4">
                                <button
                                    onClick={handleLogoutAndNavigate}
                                    className="flex items-center w-full px-4 py-3 rounded-lg transition-colors text-purple-100 hover:bg-red-600 hover:text-white group"
                                >
                                    <LogOut size={20} className="mr-4" />
                                    <span className="font-medium">Keluar</span>
                                </button>
                            </div>
                        </>
                    ) : (
                        // Regular Teacher Navigation - Use CategorySidebar (includes its own header)
                        <CategorySidebar
                            user={user}
                            page={page}
                            navigate={navigate}
                            isSidebarCollapsed={false}
                            setIsSidebarCollapsed={() => { }}
                            handleLogoutAndNavigate={handleLogoutAndNavigate}
                            aiModel={aiModel}
                            setAiModel={setAiModel}
                            isMobile={true}
                        />
                    )}
                </nav>
            </aside>

            <main className="flex-grow h-screen overflow-y-auto flex flex-col">
                {page !== 'dashboard' && page !== 'chat' && page !== 'siswa-rombel-detail' && (
                    <header className="backdrop-blur-sm border-b p-3 lg:p-4 sticky top-0 z-30 flex flex-col lg:flex-row items-center justify-between gap-3 lg:gap-4" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                        {/* Search Bar - Bottom on Mobile, Left/Center on Desktop */}
                        <div className="order-2 lg:order-1 flex-1 flex justify-center min-w-0 w-full lg:w-auto">
                            <GlobalSearch setPage={navigate} />
                        </div>

                        {/* Icons - Top on Mobile, Right on Desktop */}
                        <div className="order-1 lg:order-2 flex items-center justify-end w-full lg:w-auto gap-2 lg:gap-4 flex-shrink-0">
                            <ThemeSwitcher />
                            <CurrentPeriodIndicator user={user} onPeriodChange={navigate} />
                            <NotificationCenter onNavigateToAssignment={navigate} />
                        </div>
                    </header>
                )}

                {/* Student Alert Banner */}
                {user?.role === 'student' && (
                    <StudentAlertBanner user={user} navigate={navigate} />
                )}

                <div className="flex-grow">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default MainLayout;
